import { useState } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { IconPlus } from "@tabler/icons-react";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { CreateRequestModal } from "@/components/features/Procurement/design/CreateRequestModal";
import { ProcurementQueuePage } from "@/components/features/Procurement/design/ProcurementQueuePage";
import { QUEUE_URL_FILTERS_SCHEMA } from "@/components/features/Procurement/design/queueUrlFilters";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useRequestScope } from "@/hooks/useRequestScope";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

const procurementSearchSchema = z.object({
  tab: z.string().optional().catch(undefined),
  /**
   * Which metric tile is filtering the table. In the URL so the lens survives opening a ticket and
   * coming back, and so a list of things to close can be sent to somebody as a link.
   */
  view: z
    .enum(["breached", "awaiting", "inProgress", "open", "mine", "rentals"])
    .optional()
    .catch(undefined),
  /** SERV-3504: the Status and Work Order chips, so a filtered queue can be shared as a link. */
  ...QUEUE_URL_FILTERS_SCHEMA.shape,
});

export const Route = createFileRoute("/_authenticated/procurement/")({
  component: Procurement,
  validateSearch: procurementSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function Procurement() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  useBreadcrumb([
    {
      title: "Procurement",
      href: "/procurement",
      icon: getRouteIcon(MODULE_KEYS_MAP.procurement),
    },
  ]);

  const { view, status, wo } = useSearch({ from: "/_authenticated/procurement/" });
  const requestScope = useRequestScope();
  const { hasAccess } = usePermissions();
  const { create: canCreate, edit: canEdit } = hasAccess({ module: MODULE_KEYS_MAP.procurement });

  return (
    <>
      <ProcurementQueuePage
        key={requestScope}
        canCreate={canCreate}
        canEdit={canEdit}
        view={view}
        onViewChange={(next) =>
          void navigate({
            to: "/procurement",
            /* SERV-3504: a tile keeps the Status and Work Order chips, as it keeps the others. */
            search: { view: next ?? undefined, status, wo },
            replace: true,
          })
        }
        urlFilters={{ status, wo }}
        onUrlFiltersChange={(next) =>
          void navigate({
            to: "/procurement",
            search: { view, ...next },
            replace: true,
          })
        }
        createRequestControl={
          <Button
            variant="primary"
            iconLeft={<IconPlus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            Create Request
          </Button>
        }
      />
      <CreateRequestModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
